import '../css/app.css'
import './bootstrap'
import { createAutoAnimatePlugin } from '@formkit/addons'
import { autoAnimatePlugin } from '@formkit/auto-animate/vue'
import {
    autocomplete,
    createProPlugin,
    dropdown,
    repeater,
    taglist,
    toggle,
} from '@formkit/pro'
import { generateClasses } from '@formkit/themes'
import {
    defaultConfig as formkitDefaultConfig,
    plugin as formkitPlugin,
} from '@formkit/vue'
import { createInertiaApp } from '@inertiajs/vue3'
import {
    MutationCache,
    QueryCache,
    QueryClient,
    VueQueryPlugin,
} from '@tanstack/vue-query'
import { createPinia } from 'pinia'
import { createApp, Fragment, h } from 'vue'
import { plugin as VueTippy } from 'vue-tippy'
import { ZiggyVue } from 'ziggy-js'
import formkitTheme from './formkit.theme'
// import { plugin as inertiaPlugin } from 'formkit-addon-inertia'
import 'tippy.js/dist/tippy.css' // optional for styling

// import 'tippy.js/animations/shift-toward.css'
// import 'tippy.js/dist/backdrop.css'
// import 'tippy.js/animations/shift-away.css'
import { router } from '@inertiajs/vue3'
import { AnalyticsBrowser } from '@segment/analytics-next'
import { resolvePageComponent } from 'laravel-vite-plugin/inertia-helpers'
import { i18nVue } from 'laravel-vue-i18n'
import {
    captureError,
    initErrorCapture,
    initSentry,
    setErrorUser,
} from './lib/errorCapture'
import { POSTHOG_PROXY_HOST, PostHogPlugin } from './lib/posthog'
import {
    identifySnitcherSession,
    resetSnitcherSession,
    type SnitcherTrackerUser,
} from './lib/snitcherTracker'
import { syncZiggyWebsiteDefault } from './lib/ziggyDefaults'
import type { Admin, User } from './store/userStore'
import { brand } from './utils/themeColors'

const pro = createProPlugin('fk-6e8f4ef850', {
    autocomplete,
    dropdown,
    taglist,
    toggle,
    repeater,
})

const pinia = createPinia()
const queryClient = new QueryClient({
    queryCache: new QueryCache({
        onError: (error, query) => {
            captureError(error, {
                source: 'tanstack-query',
                query_key: JSON.stringify(query.queryKey),
            })
        },
    }),
    mutationCache: new MutationCache({
        onError: (error, _variables, _context, mutation) => {
            captureError(error, {
                source: 'tanstack-mutation',
                mutation_key: mutation.options.mutationKey
                    ? JSON.stringify(mutation.options.mutationKey)
                    : undefined,
            })
        },
    }),
    defaultOptions: {
        queries: {
            staleTime: 1000 * 60 * 5, // default cache duration for queries (5 minutes)
            retry: (failureCount, error) => {
                // Don't retry on auth errors - the axios interceptor will handle redirect
                const status = (error as { status?: number })?.status
                if (status === 401 || status === 419) {
                    return false
                }
                if (
                    error instanceof Error &&
                    (error.message.includes('401') ||
                        error.message.includes('Unauthenticated'))
                ) {
                    return false
                }
                return failureCount < 1
            },
        },
    },
})

;(async function () {
    createInertiaApp({
        progress: {
            color: brand[500],
            delay: 0,
        },
        // @ts-ignore
        resolve: name => {
            if (name.startsWith('v2/')) {
                const page = name.substring(3)
                return resolvePageComponent(
                    `./v2/${page}.vue`,
                    import.meta.glob('./v2/**/*.vue')
                )
            }
            return resolvePageComponent(
                `./pages/${name}.vue`,
                import.meta.glob('./pages/**/*.vue')
            )
        },
        setup({ el, App, props, plugin }) {
            // The server only injects Ziggy's `website` default (into the global
            // lexical `Ziggy` that route() reads) on full page loads passing
            // through SetDefaultTeamForUrlsMiddleware. Seed it from the initial
            // page's `currentWorkspace.hashid` — a PageContext prop shared on
            // every authenticated response, independent of that middleware — and
            // keep it in sync on every Inertia visit so route('inbox') & friends
            // in the persistent chrome never throw "'website' parameter is
            // required" on renders where the server default is absent (the
            // session-expiry / login-adjacent cold loads). `beforeUpdate` fires
            // with the merged new props *before* the page swap triggers the
            // reactive re-render, so the default is fresh by the time the chrome
            // recomputes its route() links. A missing hashid or config on a render
            // that should have one is reported via captureError (see ziggyDefaults).
            syncZiggyWebsiteDefault(props.initialPage)
            router.on('beforeUpdate', event => {
                syncZiggyWebsiteDefault(event.detail.page)
            })

            const app = createApp({
                render: () => h(Fragment, [h(App, props)]),
            })
                .use(plugin)
                // @ts-ignore
                .use(pinia)
                .use(VueQueryPlugin, {
                    queryClient,
                    enableDevtoolsV6Plugin: true,
                })
                .use(ZiggyVue)
                .use(autoAnimatePlugin)
                .use(
                    formkitPlugin,
                    formkitDefaultConfig({
                        plugins: [
                            pro,
                            // inertiaPlugin,
                            createAutoAnimatePlugin(),
                        ],
                        config: {
                            classes: generateClasses(formkitTheme),
                        },
                    })
                )
                .use(VueTippy, {
                    // animation: 'shift-toward',
                    defaultProps: {
                        animation: 'fade',
                        // delay: 250,
                        duration: 500,
                        placement: 'bottom',
                        arrow: false,
                        // inertia: true,
                        // animateFill: true,
                    },
                })
                .use(i18nVue, {
                    resolve: lang => import(`../../lang/${lang}.json`),
                })
                .mixin({
                    methods: {
                        // @ts-ignore
                        route: window.route,
                        // @ts-ignore
                        moment: window.moment || (() => {}),
                    },
                })

            initErrorCapture(app)
            void initSentry(app)

            const segmentWriteKey = import.meta.env.VITE_SEGMENT_WRITE_KEY

            const analytics = AnalyticsBrowser.load(
                {
                    writeKey: segmentWriteKey,
                    cdnURL: 'https://touch.snitcher.com',
                },
                {
                    integrations: {
                        'Segment.io': { apiHost: 'touch-api.snitcher.com/v1' },
                    },
                }
            )

            app.config.globalProperties.$analytics = analytics

            // Initialize PostHog. Impersonated sessions must not boot
            // posthog-js at all (no events, no replay, and no identify() —
            // identify would merge the staff device into the customer's
            // person record), so the initial page's impersonation state
            // gates initialization inside the plugin.
            app.use(PostHogPlugin, {
                apiKey:
                    import.meta.env.VITE_POSTHOG_API_KEY ||
                    'phc_FzYe9YehSSJE6tUJfaJSXIfSqkmBT6wNoeptVTrrOB8',
                apiHost:
                    import.meta.env.VITE_POSTHOG_API_HOST || POSTHOG_PROXY_HOST,
                enableSessionRecording: true,
                enableHeatmaps: true,
                capturePageview: false, // We handle this manually
                isImpersonating: Boolean(
                    (props.initialPage.props.admin as Admin | undefined)
                        ?.is_impersonating
                ),
                // Pass the initial user so PostHog sets identity and the
                // company group synchronously at init, before the first
                // pageview — otherwise early events are unattributed.
                currentUser: props.initialPage.props.user as User | undefined,
            })

            // Establish the Snitcher tracker's identify + company for the initial
            // page synchronously, alongside PostHog — so the very first events the
            // tracker autocaptures are already attributed to the user and their
            // workspace. Impersonated sessions never render the snippet (the blade
            // partial is gated on !isImpersonating), so window.Snitcher is absent
            // and the helper no-ops; the flag is a belt-and-braces guard.
            identifySnitcherSession(
                props.initialPage.props.user as SnitcherTrackerUser | undefined,
                {
                    isImpersonating: Boolean(
                        (props.initialPage.props.admin as Admin | undefined)
                            ?.is_impersonating
                    ),
                }
            )

            router.on('navigate', event => {
                const pageProps = event.detail.page.props
                const isImpersonating = (pageProps.admin as Admin)
                    ?.is_impersonating

                // Skip analytics tracking when staff is impersonating
                if (!isImpersonating) {
                    analytics.page()
                }

                // Re-establish the Snitcher identity/company on a workspace switch
                // or a re-login; clear it when the user logs out in-SPA so it
                // never leaks to the next user on a shared browser.
                const snitcherUser = pageProps.user as
                    | SnitcherTrackerUser
                    | undefined
                if (snitcherUser?.email) {
                    identifySnitcherSession(snitcherUser, { isImpersonating })
                } else if (!isImpersonating) {
                    resetSnitcherSession()
                }

                const userInfo = {
                    email:
                        (pageProps.user as User)?.email ??
                        pageProps.currentUser?.email,
                    id:
                        (pageProps.user as User)?.id ??
                        pageProps.currentUser?.hashid,
                    isAdminImpersonating: isImpersonating,
                }

                if (userInfo.id) setErrorUser(userInfo)
            })

            // Handle invalid responses (session expired, server errors)
            router.on('invalid', event => {
                const status = event.detail.response?.status

                // Session expired - 409 with X-Inertia-Location triggers hard reload
                // But if we get 401 directly, handle it
                if (status === 401) {
                    event.preventDefault()
                    window.location.href = '/login?session_expired=1'
                }
            })

            // Handle exceptions from Inertia visits
            router.on('exception', event => {
                console.error('Inertia exception:', event.detail.exception)
                captureError(event.detail.exception, {
                    source: 'inertia-router',
                })
            })

            app.mount(el)
        },
    })
})()
